/**
 * Fourth-order Runge-Kutta integrator with event handling for the rocket simulator.
 *
 * Event handling inside a timestep:
 *   1. Fuel exhaustion: if fuel reaches zero during the step, split the step at
 *      the exact exhaustion time. Integrate with current throttle until exhaustion,
 *      then with throttle = 0 for the remainder. Fuel is clamped at zero and never
 *      goes negative.
 *   2. Ground impact (z <= 0): if impact occurs during the step, use bisection to
 *      locate the impact time. For each candidate time, reintegrate from the
 *      original step-start state (not merely reevaluate derivatives). Clamp z = 0
 *      at impact and terminate.
 */

import type { Environment, RocketParams, RocketState, ControlAction } from "./types";
import { computeDerivatives } from "./dynamics";

/**
 * Single RK4 sub-step over duration dt with a constant control action.
 * Returns the new state. Does not clamp fuel or check for impact (caller handles events).
 */
function rk4Step(
  state: RocketState,
  action: ControlAction,
  env: Environment,
  params: RocketParams,
  dt: number
): RocketState {
  const deriv = (
    s: RocketState,
    a: ControlAction
  ): {
    dx: number;
    dz: number;
    dvx: number;
    dvz: number;
    dfuel: number;
    dtheta: number;
    domega: number;
  } => {
    return computeDerivatives(s, a, env, params);
  };

  const k1 = deriv(state, action);
  const s2 = addScaled(state, k1, dt * 0.5);
  const k2 = deriv(s2, action);
  const s3 = addScaled(state, k2, dt * 0.5);
  const k3 = deriv(s3, action);
  const s4 = addScaled(state, k3, dt);
  const k4 = deriv(s4, action);

  return {
    t: state.t + dt,
    x: state.x + (dt / 6) * (k1.dx + 2 * k2.dx + 2 * k3.dx + k4.dx),
    z: state.z + (dt / 6) * (k1.dz + 2 * k2.dz + 2 * k3.dz + k4.dz),
    vx: state.vx + (dt / 6) * (k1.dvx + 2 * k2.dvx + 2 * k3.dvx + k4.dvx),
    vz: state.vz + (dt / 6) * (k1.dvz + 2 * k2.dvz + 2 * k3.dvz + k4.dvz),
    fuel: state.fuel + (dt / 6) * (k1.dfuel + 2 * k2.dfuel + 2 * k3.dfuel + k4.dfuel),
    theta: state.theta + (dt / 6) * (k1.dtheta + 2 * k2.dtheta + 2 * k3.dtheta + k4.dtheta),
    omega: state.omega + (dt / 6) * (k1.domega + 2 * k2.domega + 2 * k3.domega + k4.domega),
  };
}

function addScaled(
  s: RocketState,
  d: {
    dx: number;
    dz: number;
    dvx: number;
    dvz: number;
    dfuel: number;
    dtheta: number;
    domega: number;
  },
  scale: number
): RocketState {
  return {
    t: s.t,
    x: s.x + d.dx * scale,
    z: s.z + d.dz * scale,
    vx: s.vx + d.dvx * scale,
    vz: s.vz + d.dvz * scale,
    fuel: s.fuel + d.dfuel * scale,
    theta: s.theta + d.dtheta * scale,
    omega: s.omega + d.domega * scale,
  };
}

/**
 * Integrate one full physicsDt step, splitting at fuel exhaustion if necessary.
 * Returns { state, exhaustedDuringStep }.
 * Fuel is clamped at zero; thrust is zero after exhaustion.
 */
function integrateStepWithFuelSplit(
  state: RocketState,
  action: ControlAction,
  env: Environment,
  params: RocketParams,
  dt: number
): { state: RocketState; exhaustedDuringStep: boolean } {
  // If already out of fuel, apply no thrust for the whole step.
  if (state.fuel <= 0) {
    const zeroAction: ControlAction = { throttle: 0, gimbal: action.gimbal };
    return { state: rk4Step(state, zeroAction, env, params, dt), exhaustedDuringStep: false };
  }

  // Time to fuel exhaustion at current throttle.
  const fuelFlow = params.fuelFlowRate * action.throttle;
  let exhaustedDuringStep = false;
  let currentState = state;
  let remainingDt = dt;

  if (fuelFlow > 0) {
    const timeToExhaustion = state.fuel / fuelFlow;
    if (timeToExhaustion < dt) {
      // Split: integrate with current throttle until exhaustion, then throttle=0.
      currentState = rk4Step(currentState, action, env, params, timeToExhaustion);
      // Clamp fuel exactly to zero.
      currentState = { ...currentState, fuel: 0 };
      exhaustedDuringStep = true;
      remainingDt = dt - timeToExhaustion;
      action = { throttle: 0, gimbal: action.gimbal };
    }
  }

  if (remainingDt > 0) {
    currentState = rk4Step(currentState, action, env, params, remainingDt);
    // Numerical guard: never allow negative fuel from RK4 overshoot.
    if (currentState.fuel < 0) {
      currentState = { ...currentState, fuel: 0 };
    }
  }

  return { state: currentState, exhaustedDuringStep };
}

/**
 * Reintegrate from startState to candidate time tc using sub-steps of at most subDt.
 * Used during impact bisection: the state at tc is computed by actual integration,
 * not by reevaluating derivatives at the candidate point.
 */
function integrateToTime(
  startState: RocketState,
  action: ControlAction,
  env: Environment,
  params: RocketParams,
  tc: number,
  subDt: number
): RocketState {
  let state = startState;
  let t = startState.t;
  while (t < tc) {
    const step = Math.min(subDt, tc - t);
    const result = integrateStepWithFuelSplit(state, action, env, params, step);
    state = result.state;
    t += step;
    if (state.z <= 0) {
      break;
    }
  }
  return state;
}

/**
 * Result of integrating one physicsDt step including impact detection.
 */
export interface StepResult {
  state: RocketState;
  impacted: boolean;
  impactTime: number | null;
  exhaustedDuringStep: boolean;
}

/**
 * Integrate one physicsDt step, handling fuel exhaustion and ground impact.
 * If impact occurs inside the step, bisection locates the impact time and the
 * returned state is clamped at z = 0. The simulation should terminate after impact.
 */
export function integrateStep(
  state: RocketState,
  action: ControlAction,
  env: Environment,
  params: RocketParams,
  dt: number
): StepResult {
  // First integrate the full step without impact awareness to check if z crossed zero.
  const fullStep = integrateStepWithFuelSplit(state, action, env, params, dt);

  if (fullStep.state.z > 0) {
    return {
      state: fullStep.state,
      impacted: false,
      impactTime: null,
      exhaustedDuringStep: fullStep.exhaustedDuringStep,
    };
  }

  // Impact occurred inside this step. Bisect to find t where z = 0.
  // Reintegrate from step-start state to each candidate time.
  let tLo = state.t;
  let tHi = state.t + dt;
  let stateLo = state;

  // Bisection tolerance on time (seconds).
  const tol = 1e-6;
  const subDt = Math.min(dt, 0.001);

  let impactTime = (tLo + tHi) / 2;
  let impactState = state;

  for (let iter = 0; iter < 60; iter++) {
    impactTime = (tLo + tHi) / 2;
    if (tHi - tLo < tol) {
      break;
    }

    // Reintegrate from stateLo to impactTime.
    const candidate = integrateToTime(stateLo, action, env, params, impactTime, subDt);

    if (candidate.z <= 0) {
      tHi = impactTime;
      impactState = candidate;
    } else {
      tLo = impactTime;
      stateLo = candidate;
    }
  }

  // Clamp impact state at ground.
  const clampedState: RocketState = { ...impactState, z: 0 };

  return {
    state: clampedState,
    impacted: true,
    impactTime,
    exhaustedDuringStep: fullStep.exhaustedDuringStep,
  };
}
